import React, { useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import CustomerLogApi from 'api/faber/CustomerLogAPI'
import DataApi from 'api/faber/DataApi'
import OneTimePackApi from 'api/faber/OneTimePackApi'
import AbstractAuditLogDataGrid from 'common/AbstractAuditLogDataGrid'
import MUIDataGrid from 'common/AbstractDataGrid/DataGridMUI'
import { IFilterField } from 'common/AbstractDataGridToolbar/CustomDataGridFilter'
import { getWinbackFilterOptions } from 'common/AbstractDataGridToolbar/CustomWinbackDataGridFilter'
import { CustomWinbackToolbar } from 'common/AbstractDataGridToolbar/CustomWinbackToolbar'
import ColorBox from 'common/Box/ColorWhiteBox'
import TextColorFontHeader from 'common/Typography/TextColorFontHeader'
import {
  generateColumnHeader,
  STATUS_UPDATE,
  WINBACK_LOG
} from 'helpers/column-generator/generateColumnHeader'
import OrbitSession from 'hooks/useCookies'
import {
  AddFlightRegistrationDialog,
  EmailSentLogsV2,
  NewEsimReplacement,
  SmartDelayFormLogsV2
} from 'pages/Customers/sharedComponents'
import {
  BillWaiverAuditLogType,
  CustomerDetailsLogType,
  VoucherAuditLogType
} from 'types/api/faber/customerAuditLog.type'
import { TransformedFaberUserData } from 'types/api/faber/globalCustomers.type'
import {
  GetAllFaberUsersDetails,
  GetAllInactiveAndTerminatedOtpackUserPlansDetailsType
} from 'types/api/faber/oneTimePack.type'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'
import { winbackPlansType } from 'types/api/faber/winback.type'

import CDRLogsV3 from './CDRLogsV3'
import TerminatedPlanHistory from './TerminatedPlanHistory'
import VoiceCallLogs from './VoiceCallLogs'
export default function EventLogsPage({ customerRow }: EventLogsProps) {
  const [expanded, setExpanded] = React.useState<string | false>(false)

  /*FOR WINBACK LOGS DATA GRID */
  const winbackColumns = generateColumnHeader(WINBACK_LOG)
  const acctStatusColumns = generateColumnHeader(STATUS_UPDATE)
  const [filterActive, setFilterActive] = useState<boolean>(false)
  const [filteredRows, setFilteredRows] = useState<winbackPlansType[]>([])
  const [winbackFilterFieldsChecked, setWinbackFilterFieldsChecked] = useState<
    string[]
  >([])
  const [winbackRows, setWinbackRows] = useState<WinbackType[]>([])
  const [waiverRows, setWaiverRows] = useState<BillWaiverAuditLogType[]>([])
  const [voucherRows, setVoucherRows] = useState<VoucherAuditLogType[]>([])
  const [customerData, setCustomerData] = useState<SubscriptionsTableRows>(
    {} as SubscriptionsTableRows
  )

  // FOR ACCORDION
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false)
    }

  const faberToken = OrbitSession.get('faberToken')

  const location = useLocation()
  const customerInfo = location.state as GetAllFaberUsersDetails

  //FOR DATA GRID
  const [rows, setRows] = useState<CustomerDetailsLogType[]>([])
  const [page, setPage] = useState(0)

  //eSIM STATUS API (gets ICCID)
  const [iccid, setIccid] = useState('')

  const { refetch: refetchEsimStatus } = useQuery(
    ['get-esim-status', customerRow.userID],
    async () => {
      const { data } = await DataApi.getEsimStatus(
        faberToken,
        customerRow.userID
      )
      return data
    },
    {
      onSuccess: (res) => {
        setIccid(res.data.iccid)
        return res.data
      }
    }
  )

  const {
    data: voucherLogs,
    refetch: refetchVoucherData,
    isLoading: isVoucherWaiverLogsLoading
  } = useQuery(
    ['getCustomerVouchers', customerRow.userID],
    async () => {
      const winbackVoucherLogs = await CustomerLogApi.getVoucherLogs(
        customerRow.userID,
        faberToken
      )
      return winbackVoucherLogs
    },
    {
      onError: () => {
        setVoucherRows([])
      },
      onSuccess: (res) => {
        if (res.data.data) {
          setVoucherRows(res.data.data)
        } else {
          setVoucherRows([])
        }
      },
      enabled: !!customerInfo.subscription && expanded === 'panel2'
    }
  )

  //TERMINATED USER PLAN API
  const [terminatedUserPlan, setTerminatedUserPlan] = useState<
    GetAllInactiveAndTerminatedOtpackUserPlansDetailsType[]
  >([])
  const {
    refetch: refetchTerminatedUserPlans,
    isLoading: isTerminatedUserPlansLoading
  } = useQuery(
    // the one time pack page uses customerInfo._id instead of customerInfo.user_id
    ['getAllInactiveAndTerminatedOtpackUserPlans', customerInfo._id],
    async () => {
      return OneTimePackApi.getAllTerminatedOtpackUserPlans(
        customerInfo._id,
        faberToken
      )
    },
    {
      onSuccess: (res) => {
        setTerminatedUserPlan(res.data.body.data)
      },
      onError: () => {
        return []
      },
      enabled: customerInfo.otpack && expanded === 'panel3'
    }
  )

  //WAIVER LOGS API
  const {
    data: waiverLogs,
    refetch: refetchWaiverData,
    isLoading: isBillWaiverLogsLoading
  } = useQuery(
    ['getCustomersWaivers', customerRow.userID],
    async () => {
      const winbackWaiverLogs = await CustomerLogApi.getWaiverLogs(
        customerRow.userID,
        faberToken
      )

      return winbackWaiverLogs
    },
    {
      onError: () => {
        setWaiverRows([])
      },
      onSuccess: (res) => {
        if (res.data.data) {
          setWaiverRows(res.data.data)
        } else {
          setWaiverRows([])
        }
      },
      enabled: !!customerInfo.subscription && expanded === 'panel2'
    }
  )

  const filterFields = useMemo(() => {
    const winbackFilterFields: IFilterField[] = [
      {
        values: getWinbackFilterOptions(
          winbackRows.map((row) => row.updateStatus),
          ['VOUCHER', 'WAIVER']
        ),
        checked: winbackFilterFieldsChecked,
        setChecked: setWinbackFilterFieldsChecked,
        name: 'Winback',
        index: 'Winback'
      }
    ]
    return winbackFilterFields
  }, [voucherRows, waiverRows, winbackFilterFieldsChecked, filteredRows])

  // ACCOUNT AND PLAN STATUS API
  const {
    data: dataLog,
    refetch: refetchCustomerLogs,
    isLoading: isStatusUpdateLogsLoading
  } = useQuery(
    ['getUpdateLog', customerRow.userID],
    async () => {
      const customerLogsResponse = await CustomerLogApi.getCustomerLogs(
        customerRow.userID,
        faberToken
      )
      return customerLogsResponse.data
    },
    {
      enabled: !!customerInfo.subscription && expanded === 'panel3'
    }
  )

  //Sub PLans history
  useEffect(() => {
    if (customerInfo.subscription) {
      if (dataLog && dataLog.data?.length > 0) {
        setRows(dataLog.data)
      } else {
        setRows([])
      }
    }
  }, [dataLog, customerInfo])

  //WINBACK
  useEffect(() => {
    if (customerInfo.subscription) {
      if (waiverLogs && voucherLogs) {
        setWinbackRows([...voucherRows, ...waiverRows])
      }
    }
  }, [voucherRows, waiverRows, customerInfo])

  useEffect(() => {
    refetchEsimStatus()
  }, [customerRow])

  useEffect(() => {
    if (customerInfo.subscription) {
      refetchVoucherData()
      refetchWaiverData()
      refetchCustomerLogs()
    } else if (customerInfo.otpack) {
      refetchTerminatedUserPlans()
    }
  }, [customerInfo])

  useEffect(() => {
    if (customerRow) {
      const transformedCustomerRow = {
        firstName: customerRow.firstName || '',
        lastName: customerRow.lastName || '',
        user_metadata: customerRow.user_metadata || {email: ''},
        userID: customerRow.userID || '',
        userEmail: customerRow.userEmail || '',
        email: customerRow.email || '',
        iccid: '',
        planStatus: customerRow.planStatus || '',
        planName: customerRow.planName || '',
        subscriptionStartAt: '',
        msisdn: customerRow.msisdn || '',
        cycleCount: 0,
        countryCode: customerRow.countryCode || ''
      }
      setCustomerData(transformedCustomerRow)
    }
  }, [customerRow])

  return (
    <>
      <ColorBox sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          defaultExpanded={false}
          expanded={expanded === 'panel1'}
          onChange={handleChange('panel1')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <TextColorFontHeader>eSIM Replacement Logs</TextColorFontHeader>
          </AccordionSummary>
          <AccordionDetails>
            <NewEsimReplacement
              customerRow={customerInfo}
              faberToken={faberToken}
              expanded={expanded}
              isExternal={false}
            />
          </AccordionDetails>
        </Accordion>
      </ColorBox>
      <ColorBox sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          defaultExpanded={false}
          expanded={expanded === 'panel2'}
          onChange={handleChange('panel2')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <TextColorFontHeader>All Customer Email Logs</TextColorFontHeader>
          </AccordionSummary>
          <AccordionDetails>
            <EmailSentLogsV2 
              customerRow={customerInfo}
              expanded={expanded}
              isExternal={false} />
          </AccordionDetails>
        </Accordion>
      </ColorBox>
      <ColorBox sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          defaultExpanded
          expanded={expanded === 'panel3'}
          onChange={handleChange('panel3')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <TextColorFontHeader>CDR Logs</TextColorFontHeader>
          </AccordionSummary>
          <AccordionDetails>
            <CDRLogsV3
              faberToken={faberToken}
              customerRow={customerRow}
              iccid={iccid}
              expanded={expanded}
              isExternal={false}
            />
          </AccordionDetails>
        </Accordion>
      </ColorBox>
      {customerInfo.subscription && (
        <ColorBox sx={{ mt: 2 }}>
          <Accordion
            elevation={0}
            defaultExpanded={false}
            expanded={expanded === 'panel4'}
            onChange={handleChange('panel4')}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls='panel1a-content'
              id='panel1a-header'
            >
              <TextColorFontHeader>Winback Logs</TextColorFontHeader>
            </AccordionSummary>
            <AccordionDetails>
              <MUIDataGrid
                loading={isVoucherWaiverLogsLoading || isBillWaiverLogsLoading}
                getRowId={(row) => `winback-${winbackRows.indexOf(row)}`}
                columns={winbackColumns}
                rows={filterActive ? filteredRows : winbackRows}
                tableHeight='350px'
                rowHeight={40}
                hideFooter
                disableColumnFilter
                disableColumnMenu
                disableSelectionOnClick
                pagination
                onPageChange={(newPage) => setPage(newPage)}
                components={{
                  Toolbar: CustomWinbackToolbar
                }}
                componentsProps={{
                  toolbar: {
                    filterProps: {
                      filterFields: filterFields,
                      filterActive,
                      setFilterActive,
                      rows: winbackRows,
                      setFilteredRows
                    }
                  }
                }}
                sx={{
                  '& .MuiDataGrid-iconSeparator': {
                    visibility: 'hidden'
                  },
                  '& .MuiDataGrid-footerContainer': {
                    border: '0'
                  },
                  '& .MuiDataGrid-cellContent': {
                    fontSize: '14px'
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: '14px',
                    fontWeight: '500'
                  },
                  '& .MuiDataGrid-cell': {
                    border: '0px'
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    border: '1',
                    borderColor: 'black'
                  },
                  '&.MuiDataGrid-root .MuiDataGrid-cell:focus': {
                    outline: 'none'
                  },
                  mx: '-10px'
                }}
              />
            </AccordionDetails>
          </Accordion>
        </ColorBox>
      )}
      <ColorBox sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          defaultExpanded
          expanded={expanded === 'panel5'}
          onChange={handleChange('panel5')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <TextColorFontHeader>Packs History</TextColorFontHeader>
          </AccordionSummary>
          {customerInfo.otpack ? (
            isTerminatedUserPlansLoading ? (
              <CircularProgress />
            ) : (
              <TerminatedPlanHistory terminatedUserPlan={terminatedUserPlan} />
            )
          ) : (
            <AccordionDetails>
              <AbstractAuditLogDataGrid
                loading={isStatusUpdateLogsLoading}
                getRowId={(row) => rows.indexOf(row)}
                rows={rows}
                columns={acctStatusColumns}
                pagination
                page={page}
                onPageChange={(newPage) => setPage(newPage)}
                pageSize={5}
                tableHeight='350px'
                rowHeight={50}
              />
            </AccordionDetails>
          )}
        </Accordion>
      </ColorBox>
      <ColorBox sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          defaultExpanded={false}
          expanded={expanded === 'panel6'}
          onChange={handleChange('panel6')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <TextColorFontHeader>Smart Delay Forms</TextColorFontHeader>
          </AccordionSummary>
          <AccordionDetails>
            <AddFlightRegistrationDialog 
              userID={customerRow.userID}
            />
            <br></br>
            {customerData && (
              <SmartDelayFormLogsV2
                faberToken={faberToken}
                customerRow={customerData}
                expanded={expanded}
                isExternal={false}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </ColorBox>
      <ColorBox sx={{ mt: 2 }}>
        <Accordion
          elevation={0}
          defaultExpanded
          expanded={expanded === 'panel8'}
          onChange={handleChange('panel8')}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls='panel1a-content'
            id='panel1a-header'
          >
            <TextColorFontHeader>Voice Call Logs</TextColorFontHeader>
          </AccordionSummary>
          <AccordionDetails>
            <VoiceCallLogs
              faberToken={faberToken}
              customerRow={customerRow}
              expanded={expanded}
            />
          </AccordionDetails>
        </Accordion>
      </ColorBox>
    </>
  )
}
interface EventLogsProps {
  customerRow: TransformedFaberUserData
}

type WinbackType = BillWaiverAuditLogType | VoucherAuditLogType
